import React, { useEffect, useMemo, useState, useRef } from "react";
import { message, Modal, Tooltip } from "antd";
import { ChatMessage, TagAssociatedDocument, TemplateTag } from "@/lib/types";
import { useFontStyle } from "@/context/StyleContext";
import QualityCheckStatus from "./QualityCheckStatus/QualityCheckStatus";
import SectionGeneratorDecision from "./SectionGeneratorDecision/SectionGeneratorDecision";
import AgentPanel from "./AgentPanel/AgentPanel";
import SectionGeneratorActionButtons from "./SectionGeneratorActionButtons/SectionGeneratorActionButtons";
import SectionGeneratorHeader from "./SectionGeneratorHeader/SectionGeneratorHeader";
import SectionTagDocumentsModal from "./SectionTagDocumentsModal/SectionTagDocumentsModal";
import SectionTagsDisclosure from "./SectionTagsDisclosure/SectionTagsDisclosure";
import SectionToggles from "./SectionToggles/SectionToggles";
import SectionGeneratorFeedback from "./SectionGeneratorFeedback";
import Logger from "@/utility/logger";
import { downloadAndOpenBase64File } from "@/utility/documentDownload";
import { getRestoredSectionSyncState, hasSectionActiveTask, markSectionRegenerationStarted } from "@/utility/sectionTaskState";
import { isMissingSectionBookmarkError } from "@/utility/wordBookmarkErrors";
import { useAuth } from "@/context/AuthContext";
import { useEnvironment } from "@/context/EnvironmentContext";
import { closeActiveViewerDialog } from "@/utility/officeDialogManager";
import { beginGraphViewOpenStatus, viewerOpenFailureMessage } from "@/utility/viewerClosingStatus";
import { openGraphViewDialog, storeGraphViewPayload, type GraphProjectDocument } from "@/utility/graphViewStorage";

const GRAPH_VIEW_OPEN_TIMEOUT_MS = 15000;
const GRAPH_VIEW_DOCUMENTS_TIMEOUT_MS = 8000;

function SectionGeneratorPanel({
  sections,
  templateTagsMap,
  setSelectAllSections,
  selectAllSections,
  generatedSections,
  selectedSections,
  setSelectedSections,
  sectionStatus,
  sectionEvalStatus,
  setSectionEvalStatus,
  outputDecision,
  setOutputDecision,
  acceptChanges,
  runTask,
  scrollToSection,
  setSectionDialogContent,
  setSourceDialogOpen,
  setSourceDialogContent,
  setFeedbackDialogOpen,
  setFeedbackDialogContent,
  setRegenerateDialogContent,
  setRegenerateDialogOpen,
  setQualityCheckDialogOpen,
  setQualityCheckDialogContent,
  trackChanges,
  handleTrackChangesToggle,
  bookmarks,
  projectDetailsExpanded,
  sectionGeneration,
  selectedProject,
  selectedReportTemplate,
  groundingByTaskId,
  groundingLoadingTaskIds,
  groundingErrorsByTaskId,
  fetchGroundingArtifact: _unusedFetchGroundingArtifact,
  retrieveDocumentsList,
  retrieveSourceDocument,
  // uploadedFiles,
  // setUploadedFiles,
  uploadDocument,
  deleteDocument,
  deleteTag,
  regenerateDialogContent,
  setShowError,
  titleMap,
  deleteTagsFiles,
  scrollToLastGenerated,
  uploadedAgentFiles,
  setUploadedAgentFiles,
  transcribeAudio,
  handleSectionGeneration,
  setIsAgentSectionRegenerationOn,
  setAgenticRegenerationRequest,
  submitLangfuseFeedback,
  feedbackStates,
  setFeedbackStates,
  setGeneratedSections,
  logAuditAction,
  reportTemplateProjectId,
  onGraphViewControllerChange,
}) {
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { isSuperAdmin, tokens, organizationId } = useAuth();
  const { selectedEnvironment } = useEnvironment();

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const agentPanelRefs = useRef(new Map<string, HTMLDivElement>());

  const [isDragOver, setIsDragOver] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState<Set<string>>(new Set());
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [isSpecialSectionGenerating, setIsSpecialSectionGenerating] = useState(false);
  const [isGraphViewOpening, setIsGraphViewOpening] = useState(false);
  const [expandedTagSectionId, setExpandedTagSectionId] = useState<string | null>(null);
  const [tagDocumentsByKey, setTagDocumentsByKey] = useState<Record<string, TagAssociatedDocument[]>>({});
  const [tagDocumentsLoading, setTagDocumentsLoading] = useState<Record<string, boolean>>({});
  const [tagDocumentsErrors, setTagDocumentsErrors] = useState<Record<string, string | null>>({});
  const [tagDocumentsModalState, setTagDocumentsModalState] = useState<{
    sectionId: string;
    sectionDisplayName: string;
    tag: TemplateTag;
  } | null>(null);

  useEffect(() => {
    const specialSectionGeneration = async () => {
      try {
        await handleSectionGeneration();
      } catch (error) {
        Logger.error("SectionGeneratorPanel.tsx: Error during section generation:", error);
      } finally {
        setIsSpecialSectionGenerating(false);
      }
    };

    if (isSpecialSectionGenerating) {
      specialSectionGeneration();
    }
  }, [isSpecialSectionGenerating]);

  useEffect(() => {
    setExpandedTagSectionId(null);
    setTagDocumentsByKey({});
    setTagDocumentsLoading({});
    setTagDocumentsErrors({});
    setTagDocumentsModalState(null);
  }, [selectedProject?.id, templateTagsMap]);

  useEffect(() => {
    if (expandedTagSectionId && !sections.some((section) => section.id === expandedTagSectionId)) {
      setExpandedTagSectionId(null);
      setTagDocumentsModalState((prevState) =>
        prevState && prevState.sectionId === expandedTagSectionId ? null : prevState
      );
    }
  }, [sections, expandedTagSectionId]);

  const orderedSections = useMemo(() => {
    if (!Array.isArray(sections)) {
      return [];
    }

    return sections
      .filter((section) => section.type.toLowerCase() === "section" || section.type.toLowerCase() === "section_agentic")
      .slice()
      .sort((a, b) => {
        const indexA = bookmarks.indexOf(a.insertion_key + "_START"),
          indexB = bookmarks.indexOf(b.insertion_key + "_START"),
          aInactive = !generatedSections[a.id] && sectionStatus[a.id] === false,
          bInactive = !generatedSections[b.id] && sectionStatus[b.id] === false;
        return indexA !== -1 && indexB !== -1
          ? indexA - indexB
          : indexA !== -1
          ? -1
          : indexB !== -1
          ? 1
          : aInactive && !bInactive
          ? 1
          : !aInactive && bInactive
          ? -1
          : a.display_name.localeCompare(b.display_name);
      });
  }, [sections, bookmarks, generatedSections, sectionStatus]);

  const hasAnyActiveSectionTask = useMemo(
    () => generatedSections.some((generatedSection) => hasSectionActiveTask(generatedSection)),
    [generatedSections]
  );
  const graphViewOpeningRef = useRef(false);
  const graphViewOpeningTimeoutRef = useRef<number | null>(null);
  const isGraphViewDisabled = Boolean(
    isGraphViewOpening || sectionGeneration || isSpecialSectionGenerating || hasAnyActiveSectionTask
  );

  const clearGraphViewOpeningTimeout = () => {
    if (graphViewOpeningTimeoutRef.current !== null) {
      window.clearTimeout(graphViewOpeningTimeoutRef.current);
      graphViewOpeningTimeoutRef.current = null;
    }
  };

  useEffect(
    () => () => {
      clearGraphViewOpeningTimeout();
    },
    []
  );

  const withTimeout = async <T,>(
    promise: Promise<T>,
    timeoutMs: number,
    timeoutMessage: string,
    onTimeout?: () => void
  ): Promise<T> =>
    await new Promise<T>((resolve, reject) => {
      const timeout = window.setTimeout(() => {
        onTimeout?.();
        reject(new Error(timeoutMessage));
      }, timeoutMs);

      promise
        .then(resolve)
        .catch(reject)
        .finally(() => window.clearTimeout(timeout));
    });

  const retrieveAllProjectDocumentsForGraph = async (): Promise<GraphProjectDocument[]> => {
    if (!selectedProject?.id || !retrieveDocumentsList) {
      return [];
    }

    const documents: GraphProjectDocument[] = [];
    let page = 1;
    let totalPages = 1;

    do {
      const response = await retrieveDocumentsList({
        projectId: selectedProject.id,
        page,
        perPage: 100,
      });

      totalPages = response?.pages || 1;
      documents.push(...(response?.items || []));
      page += 1;
    } while (page <= totalPages);

    return documents.filter((document, index, items) => {
      const documentKey = document?.document_id || document?.id || document?.filename || document?.name;
      if (!documentKey) {
        return true;
      }
      return (
        items.findIndex((candidate) => {
          const candidateKey =
            candidate?.document_id || candidate?.id || candidate?.filename || candidate?.name;
          return candidateKey === documentKey;
        }) === index
      );
    });
  };

  const handleVoiceTranscription = (transcription: string) => {
    // Insert the transcription into the input
    setInputValue((prev) => {
      const newValue = prev ? `${prev} ${transcription}` : transcription;
      return newValue;
    });

    // Focus the textarea after transcription
    setTimeout(() => {
      textareaRef.current?.focus();
    }, 100);
  };

  const handleClearInput = () => {
    setInputValue("");
    textareaRef.current?.focus();
  };

  const buildTagDocumentsKey = (sectionId: string, tagId: string) => `${sectionId}::${tagId}`;

  const getSectionTags = (section): TemplateTag[] => {
    const includeTagIds = Array.isArray(section.include_tag_ids)
      ? Array.from(new Set<string>(section.include_tag_ids as string[]))
      : [];

    return includeTagIds.map((tagId) => {
      const resolvedTag = templateTagsMap?.[tagId];

      if (resolvedTag) {
        return resolvedTag;
      }

      return {
        id: tagId,
        name: tagId,
        color: "#86868b",
      };
    });
  };

  const usesSectionTags = (section) => {
    const displayName = String(section?.display_name || "").toLowerCase();
    return !displayName.includes("executive summary") && !displayName.includes("abbreviation");
  };

  const toggleSectionTags = (sectionId: string) => {
    setExpandedTagSectionId((prevSectionId) => (prevSectionId === sectionId ? null : sectionId));
  };

  const getTagDocumentsByKey = (documentsKey: string | null) => {
    return documentsKey ? tagDocumentsByKey[documentsKey] || [] : [];
  };

  const getTagDocumentsLoadingByKey = (documentsKey: string | null) => {
    return documentsKey ? tagDocumentsLoading[documentsKey] || false : false;
  };

  const getTagDocumentsErrorByKey = (documentsKey: string | null) => {
    return documentsKey ? tagDocumentsErrors[documentsKey] || null : null;
  };

  const fetchTagDocuments = async (sectionId: string, tagId: string) => {
    const nextKey = buildTagDocumentsKey(sectionId, tagId);

    if (tagDocumentsByKey[nextKey] || tagDocumentsLoading[nextKey]) {
      return;
    }

    if (!selectedProject?.id) {
      setTagDocumentsErrors((prev) => ({
        ...prev,
        [nextKey]: "No project selected for document lookup.",
      }));
      return;
    }

    setTagDocumentsLoading((prev) => ({
      ...prev,
      [nextKey]: true,
    }));
    setTagDocumentsErrors((prev) => ({
      ...prev,
      [nextKey]: null,
    }));

    try {
      let page = 1;
      let totalPages = 1;
      const collectedDocuments: TagAssociatedDocument[] = [];

      do {
        const response = await retrieveDocumentsList({
          projectId: selectedProject.id,
          tagIds: [tagId],
          page,
          perPage: 100,
        });

        totalPages = response?.pages || 1;

        const currentDocuments = (response?.items || []).map((document) => ({
          id: document.id,
          filename: document.filename || document.name || document.id,
        }));

        collectedDocuments.push(...currentDocuments);
        page += 1;
      } while (page <= totalPages);

      const uniqueDocuments = collectedDocuments.filter(
        (document, index, items) => items.findIndex((item) => item.id === document.id) === index
      );

      setTagDocumentsByKey((prev) => ({
        ...prev,
        [nextKey]: uniqueDocuments,
      }));
    } catch (error) {
      Logger.error("SectionGeneratorPanel.tsx: Failed to load tag documents", error);
      setTagDocumentsErrors((prev) => ({
        ...prev,
        [nextKey]: error?.message || "Failed to load associated files.",
      }));
    } finally {
      setTagDocumentsLoading((prev) => ({
        ...prev,
        [nextKey]: false,
      }));
    }
  };

  const openTagDocumentsModal = (section, tag: TemplateTag) => {
    setTagDocumentsModalState({
      sectionId: section.id,
      sectionDisplayName: section.display_name,
      tag,
    });

    void fetchTagDocuments(section.id, tag.id);
  };

  const closeTagDocumentsModal = () => {
    setTagDocumentsModalState(null);
  };

  const downloadTagDocument = async (documentId: string) => {
    try {
      const fileData = await retrieveSourceDocument(documentId);
      downloadAndOpenBase64File(fileData);
    } catch (error) {
      Logger.error("SectionGeneratorPanel.tsx: Failed to download tag document", error);
      setShowError({
        isVisible: true,
        type: "error",
        title: titleMap[error.name],
        message: error.name,
        details: {
          message: error.message,
          code: error.code,
          stack: error.stack,
          context: undefined,
        },
      });
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      content: inputValue.trim(),
      role: "user",
      timestamp: new Date(),
    };

    Logger.debug("SectionGeneratorPanel.tsx: Sending message:", userMessage);
  };

  const { userStylesEnabled, setUserStylesEnabled } = useFontStyle();
  // Scrolls to the top of the generated sections panel on page load
  useEffect(() => {
    const scrollToTop = () => {
      const panel = document.querySelector(".section_generator_panel_content");
      if (panel) {
        panel.scrollTop = 0;
      }
      const home = document.querySelector(".home_content");
      if (home) {
        home.scrollTop = 0;
      }
      const list = document.querySelector(".section_generator_panel_list");
      if (list) {
        list.scrollTop = 0;
      }
    };
    scrollToTop();
    if (typeof requestAnimationFrame !== "undefined") {
      requestAnimationFrame(scrollToTop);
    }
    const t1 = setTimeout(scrollToTop, 100);
    const t2 = setTimeout(scrollToTop, 300);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, []);

  // Handles toggling the selection of all sections
  const toggleSelectAll = () => {
    const selectableSections = sections.filter(
      (section) => !generatedSections.some((gs) => gs.id === section.id) && sectionStatus[section.id] !== false
    );

    if (selectedSections.length === selectableSections.length) {
      setSelectAllSections(false);
      setSelectedSections([]);
    } else {
      setSelectAllSections(true);
      setSelectedSections(selectableSections);
    }
  };

  // Returns a short section ID from the section name
  const getSectionId = (section_name: string) => {
    const firstUnderscoreIndex = section_name.indexOf("_");

    if (firstUnderscoreIndex === -1 || section_name.length === 0) {
      return null;
    }

    const idPart = section_name.substring(firstUnderscoreIndex + 1);

    const reformattedId = idPart.replace(/_/g, ".");

    return reformattedId;
  };

  // Handles the flow for accepting changes for a section
  const handleAccept = async (section) => {
    try {
      const downloadedSection = generatedSections.find((generated) => generated.id === section.id)?.fromReportDownload;
      await acceptChanges(section.insertion_key);
      setOutputDecision((prev) => ({
        ...prev,
        [section.id]: false,
      }));
      if (!downloadedSection) {
        await deleteTagsFiles(section.id);
      }
      // Audit trail: log UPDATE action for keeping/accepting a section
      if (reportTemplateProjectId && !downloadedSection) {
        logAuditAction(reportTemplateProjectId, "UPDATE", `Keep Changes: ${section.display_name}`);
      }
    } catch (error) {
      if (isMissingSectionBookmarkError(error)) {
        message.warning(error.message);
        return;
      }

      Logger.error("SectionGeneratorPanel.tsx: Error accepting changes:", error);
      setShowError({
        isVisible: true,
        type: "error",
        title: titleMap[error.name],
        message: error.name,
        details: {
          message: error.message,
          code: error.code,
          stack: error.stack,
          context:
            error.name === "RichApi.Error"
              ? {
                  errorLocation: error.debugInfo?.errorLocation,
                  statement: error.debugInfo?.statement,
                  surroundingStatements: error.debugInfo?.surroundingStatements,
                }
              : undefined,
        },
      });
    }
  };

  // Handles the flow for regenerating a section
  const handleRegenerate = async (section) => {
    try {
      const matchedSection = generatedSections.find((s) => s.id === section.id);

      Logger.trace("SectionGeneratorPanel.tsx: Section regeneration initiated", {
        action: "section_regeneration",
        sectionId: section.id,
        sectionName: section.display_name,
      });

      setRegenerateDialogContent((prev) => ({
        ...prev,
        chat_id: matchedSection?.chat_history_id,
        section: matchedSection,
        freeform: "",
        options: [],
      }));
      setSectionDialogContent({ section: matchedSection });

      if (section.type.toLowerCase() === "section_agentic") {
        Modal.confirm({
          title: section.display_name,
          icon: null,
          content: "Do you want to regenerate this section?",
          async onOk() {
            const requestId = `${section.id}-${Date.now()}`;
            setRegenerateDialogOpen(false);
            setIsAgentSectionRegenerationOn(false);
            setGeneratedSections((prevSections) =>
              prevSections.map((generatedSection) =>
                generatedSection.id === section.id
                  ? markSectionRegenerationStarted(generatedSection, "agentic")
                  : generatedSection
              )
            );
            setAgenticRegenerationRequest({
              requestId,
              section: matchedSection || section,
            });
          },
          onCancel() {
            setIsAgentSectionRegenerationOn(false);
            Logger.debug("SectionGeneratorPanel.tsx: User clicked cancel, do nothing.");
          },
          okText: "Continue",
        });
      } else {
        setRegenerateDialogOpen(true);
      }
    } catch (error) {
      Logger.error("SectionGeneratorPanel.tsx: Error regenerating section:", error);
      setShowError({
        isVisible: true,
        type: "error",
        title: titleMap[error.name],
        message: error.name,
        details: {
          message: error.message,
          code: error.code,
          stack: error.stack,
          context: undefined,
        },
      });
    }
  };

  // Handles the flow for declining changes for a section
  const handleDecline = async (section) => {
    try {
      setFeedbackDialogContent((prev) => ({
        ...prev,
        chat_id: generatedSections.find((s) => s.id === section.id)?.chat_history_id,
        section: generatedSections.find((s) => s.id === section.id),
      }));
      setSectionDialogContent({ section: generatedSections.find((s) => s.id === section.id) });
      setFeedbackDialogOpen(true);
      // sectionStatus[section.id] = true;
    } catch (error) {
      Logger.error("SectionGeneratorPanel.tsx: Error declining changes:", error);
      setShowError({
        isVisible: true,
        type: "error",
        title: titleMap[error.name],
        message: error.name,
        details: {
          message: error.message,
          code: error.code,
          stack: error.stack,
          context: {},
        },
      });
    }
  };


  useEffect(() => {
    const loadPersistedFeedback = async () => {
      if (!generatedSections || generatedSections.length === 0) return;

      const loadedStates: typeof feedbackStates = {};

      generatedSections.forEach((section) => {
        if (section.feedback) {
          const storedData = section?.feedback;

          loadedStates[section.id] = {
            thumbs: storedData?.thumbs,
            comment: "",
            isSubmitted: false,
            showCommentBox: false,
          };
        }
      });

      if (Object.keys(loadedStates).length > 0) {
        setFeedbackStates((prev) => ({ ...prev, ...loadedStates }));
      }
    };

    loadPersistedFeedback();
  }, []);

  const persistFeedbackToDocument = async (sectionId: string, chat_history_id: string, isThumbsUp: boolean) => {
    setGeneratedSections((prevSections) =>
      prevSections.map((section) => {
        if ((chat_history_id && section.chat_history_id === chat_history_id) || section.id === sectionId) {
          return {
            ...section,
            feedback: {
              thumbs: isThumbsUp,
              comment: "",
            },
          };
        }
        return section;
      })
    );
  };

  const submitFeedback = async (chat_history_id: string, thumbs: boolean, comment?: string) => {
    if (!chat_history_id) return;
    if (generatedSections.some((section) => section.fromReportDownload && section.chat_history_id === chat_history_id)) {
      return;
    }

    let feedback_score = thumbs ? 4 : 2;

    const commentText = comment?.trim() ? comment : "No feedback comment received";

    let sectionFeedback = `Jenly-Add-in.Section-Panel.User-Feedback: ${commentText}`;

    try {
      await submitLangfuseFeedback(chat_history_id, feedback_score, sectionFeedback);
    } catch (error) {
      Logger.error("SectionGeneratorPanel.tsx: Error submitting feedback:", error);
    }
  };

  const handleInitialFeedback = async (sectionId: string, chatHistoryId: string, thumbs: boolean) => {
    if (thumbs) {
      setFeedbackStates((prev) => ({
        ...prev,
        [sectionId]: { thumbs: true, comment: "", isSubmitted: true, showCommentBox: false },
      }));

      await Promise.all([
        persistFeedbackToDocument(sectionId, chatHistoryId, true),
        submitFeedback(chatHistoryId, true),
      ]);
    } else {
      setFeedbackStates((prev) => ({
        ...prev,
        [sectionId]: { thumbs: false, comment: "", isSubmitted: false, showCommentBox: true },
      }));

      await Promise.all([
        persistFeedbackToDocument(sectionId, chatHistoryId, false),
        submitFeedback(chatHistoryId, false),
      ]);
    }
  };

  const handleCommentSubmit = async (sectionId: string, chatHistoryId: string) => {
    const state = feedbackStates[sectionId];
    if (!state || state.thumbs === null || state.isSubmitting) return;

    setFeedbackStates((prev) => ({
      ...prev,
      [sectionId]: { ...prev[sectionId], isSubmitting: true },
    }));

    try {
      await submitFeedback(chatHistoryId, state.thumbs, state.comment);

      setFeedbackStates((prev) => ({
        ...prev,
        [sectionId]: { ...prev[sectionId], isSubmitted: true, showCommentBox: false, isSubmitting: false },
      }));
    } catch (error) {
      Logger.error("SectionGeneratorPanel.tsx: Failed to submit feedback", error);
      setFeedbackStates((prev) => ({
        ...prev,
        [sectionId]: { ...prev[sectionId], isSubmitting: false },
      }));
    }
  };

  const openGraphViewWindow = async () => {
    if (isGraphViewDisabled || graphViewOpeningRef.current) {
      return;
    }

    closeActiveViewerDialog();
    graphViewOpeningRef.current = true;
    setIsGraphViewOpening(true);
    const openStatus = beginGraphViewOpenStatus();
    clearGraphViewOpeningTimeout();
    let graphViewOpenTimedOut = false;
    graphViewOpeningTimeoutRef.current = window.setTimeout(() => {
      if (!graphViewOpeningRef.current) {
        return;
      }

      graphViewOpenTimedOut = true;
      openStatus.done();
      graphViewOpeningRef.current = false;
      setIsGraphViewOpening(false);
      message.warning("Opening the graph view is taking longer than expected. Please try again.");
    }, GRAPH_VIEW_OPEN_TIMEOUT_MS);

    try {
      let projectDocuments: GraphProjectDocument[] = [];
      try {
        projectDocuments = await withTimeout(
          retrieveAllProjectDocumentsForGraph(),
          GRAPH_VIEW_DOCUMENTS_TIMEOUT_MS,
          "Project document loading timed out.",
          () => Logger.warn("SectionGeneratorPanel.tsx: Timed out loading project documents for graph view.")
        );
      } catch (error) {
        Logger.error("SectionGeneratorPanel.tsx: Failed to load project documents for graph view:", error);
      }

      const payloadKey = await storeGraphViewPayload({
        project: selectedProject,
        reportTemplate: selectedReportTemplate,
        sections: orderedSections,
        generatedSections,
        projectDocuments,
        groundingByTaskId,
        groundingLoadingTaskIds,
        groundingErrorsByTaskId,
        api_base_url: selectedEnvironment?.url || null,
        access_token: tokens?.accessToken ?? null,
        // Lets the dialog refresh an expired access token itself
        // instead of failing every call after the token TTL.
        refresh_token: tokens?.refreshToken ?? null,
        organization_id: organizationId || selectedProject?.organization_id || null,
        is_super_admin: isSuperAdmin,
      });
      await openGraphViewDialog(payloadKey, { onClosingStatus: openStatus.onClosingStatus });
    } catch (error) {
      Logger.error("SectionGeneratorPanel.tsx: Failed to open graph view window:", error);
      if (!graphViewOpenTimedOut) {
        message.error(
          viewerOpenFailureMessage(openStatus.describeFailure(error), "Unable to open the graph view. Please try again.")
        );
      }
    } finally {
      openStatus.done();
      graphViewOpeningRef.current = false;
      clearGraphViewOpeningTimeout();
      setIsGraphViewOpening(false);
    }
  };

  // The graph is launched from the Sources page, which has no access to this
  // panel's sections and grounding state. The launcher is read through a ref so
  // its identity stays stable and the effect only fires when availability changes.
  const openGraphViewWindowRef = useRef(openGraphViewWindow);
  openGraphViewWindowRef.current = openGraphViewWindow;
  useEffect(() => {
    onGraphViewControllerChange?.({
      open: () => {
        void openGraphViewWindowRef.current();
      },
      disabled: isGraphViewDisabled,
      opening: isGraphViewOpening,
    });
  }, [isGraphViewDisabled, isGraphViewOpening, onGraphViewControllerChange]);

  const activeTagDocumentsKey = tagDocumentsModalState
    ? buildTagDocumentsKey(tagDocumentsModalState.sectionId, tagDocumentsModalState.tag.id)
    : null;

  return (
    <div className={`section_generator_panel_content ${projectDetailsExpanded ? "expanded" : "collapsed"}`}>
      {/* Keep Select All / Track Changes
       * reachable while the user scrolls a long section list. The wrapper
       * has its own background so the section items do not show under
       * the toggles. */}
      <div className="section_generator_panel_toggles_sticky">
      <SectionToggles
        toggleSelectAll={toggleSelectAll}
        selectAllSections={selectAllSections}
        sectionGeneration={sectionGeneration}
        handleTrackChangesToggle={async (checked: boolean) => {
          await handleTrackChangesToggle(checked);
          // Audit trail: log UPDATE action for track changes toggle in section panel
          if (reportTemplateProjectId) {
            logAuditAction(reportTemplateProjectId, "UPDATE", `Track Changes Toggle: ${checked ? "On" : "Off"}`);
          }
        }}
        trackChanges={trackChanges}
      />
      </div>

      <ul className="section_generator_panel_list">
        {orderedSections.map((section) => {
          const generatedSection = generatedSections.find((s) => s.id === section.id);
          const isGeneratedSection = Boolean(generatedSection);
          const sectionHasActiveTask = hasSectionActiveTask(generatedSection);
          const shouldShowChatAsyncUpdates =
            section.type.toLowerCase() === "section" && (generatedSection?.agentUpdates || []).length > 0;
          const restoredPanelState = getRestoredSectionSyncState(generatedSection);
          const showRestoredOrActivePanel =
            section.type.toLowerCase() === "section" &&
            !shouldShowChatAsyncUpdates &&
            (restoredPanelState.isActive || restoredPanelState.isTerminal);
          const terminalAgentUpdate = (generatedSection?.agentUpdates || []).some((update) =>
            ["done", "completed"].includes(String(update?.status || "").toLowerCase())
          );
          const isNonAgenticGenerationComplete =
            section.type.toLowerCase() === "section" && Boolean(generatedSection?.chat_history_id);
          const isAgenticGenerationComplete =
            section.type.toLowerCase() === "section_agentic" &&
            Boolean(generatedSection?.content) &&
            terminalAgentUpdate;
          const isSectionGenerationComplete =
            !sectionHasActiveTask && (isNonAgenticGenerationComplete || isAgenticGenerationComplete);
          const showTagButton = usesSectionTags(section);
          const isSectionTagsOpen = showTagButton && expandedTagSectionId === section.id;
          const isBlockedByOtherSection = hasAnyActiveSectionTask && !sectionHasActiveTask;

          return (
            <li
              id={section.id}
              key={section.id}
              className={`section_generator_panel_item ${isGeneratedSection ? "generated" : ""}`}
              style={{
                background: isGeneratedSection
                  ? "#e0f7fa"
                  : selectedSections.some((s) => s.id === section.id)
                  ? "#f5faff"
                  : section.user_query === ""
                  ? "#ea9999"
                  : sectionStatus[section.id] === false
                  ? "#f2e7c4"
                  : "white",
              }}
            >
              <div className="section_generator_panel_item_container">
                <SectionGeneratorHeader
                  sectionStatus={sectionStatus}
                  section={section}
                  generatedSections={generatedSections}
                  sectionGeneration={sectionGeneration}
                  setSelectedSections={setSelectedSections}
                  sections={sections}
                  selectedSections={selectedSections}
                  setIsSpecialSectionGenerating={setIsSpecialSectionGenerating}
                  scrollToSection={scrollToSection}
                />
                <SectionGeneratorActionButtons
                  generatedSections={generatedSections}
                  section={section}
                  sectionEvalStatus={sectionEvalStatus}
                  setQualityCheckDialogContent={setQualityCheckDialogContent}
                  setSectionDialogContent={setSectionDialogContent}
                  setQualityCheckDialogOpen={setQualityCheckDialogOpen}
                  outputDecision={outputDecision}
                  handleRegenerate={handleRegenerate}
                  setSourceDialogContent={setSourceDialogContent}
                  setSourceDialogOpen={setSourceDialogOpen}
                  toggleSectionTags={toggleSectionTags}
                  isSectionTagsOpen={isSectionTagsOpen}
                  showTagButton={showTagButton}
                />
              </div>
              {isGeneratedSection && (
                <div className="section_generator_panel_generated_background">
                  <div className="section_generator_panel_generated_eval">
                    {(section.type.toLowerCase() !== "section" || shouldShowChatAsyncUpdates || showRestoredOrActivePanel) && (
                      <AgentPanel
                        agentPanelRefs={agentPanelRefs}
                        section={section}
                        generatedSections={generatedSections}
                        selectedProject={selectedProject}
                        fileInputRef={fileInputRef}
                        isDragOver={isDragOver}
                        setIsDragOver={setIsDragOver}
                        uploadedAgentFiles={uploadedAgentFiles}
                        setUploadedAgentFiles={setUploadedAgentFiles}
                        uploadingFiles={uploadingFiles}
                        setUploadingFiles={setUploadingFiles}
                        uploadDocument={uploadDocument}
                        deleteDocument={deleteDocument}
                        deleteTag={deleteTag}
                        regenerateDialogContent={regenerateDialogContent}
                        textareaRef={textareaRef}
                        inputValue={inputValue}
                        setInputValue={setInputValue}
                        handleKeyPress={handleKeyPress}
                        isLoading={isLoading}
                        handleClearInput={handleClearInput}
                        handleVoiceTranscription={handleVoiceTranscription}
                        setShowError={setShowError}
                        titleMap={titleMap}
                        transcribeAudio={transcribeAudio}
                        handleSendMessage={handleSendMessage}
                        logAuditAction={logAuditAction}
                        reportTemplateProjectId={reportTemplateProjectId}
                        sectionDisplayName={section.display_name}
                      />
                    )}
                  </div>
                  <SectionGeneratorDecision
                    outputDecision={outputDecision}
                    section={{ ...section, ...generatedSection }}
                    handleAccept={handleAccept}
                    handleRegenerate={handleRegenerate}
                    handleDecline={handleDecline}
                    isRegenerating={sectionHasActiveTask}
                    isDisabled={sectionHasActiveTask || isBlockedByOtherSection}
                    disabledReason={isBlockedByOtherSection ? "Section generation in progress" : undefined}
                  />

                  {isSectionGenerationComplete && (
                    <SectionGeneratorFeedback
                      feedbackStates={feedbackStates}
                      section={section}
                      handleInitialFeedback={handleInitialFeedback}
                      generatedSections={generatedSections}
                      setFeedbackStates={setFeedbackStates}
                      handleCommentSubmit={handleCommentSubmit}
                    />
                  )}
                </div>
              )}
              {isSectionTagsOpen && (
                <SectionTagsDisclosure
                  onOpenTagDocuments={(tag) => openTagDocumentsModal(section, tag)}
                  sectionId={section.id}
                  tags={getSectionTags(section)}
                />
              )}
            </li>
          );
        })}
      </ul>
      <SectionTagDocumentsModal
        downloadDocument={downloadTagDocument}
        documents={getTagDocumentsByKey(activeTagDocumentsKey)}
        error={getTagDocumentsErrorByKey(activeTagDocumentsKey)}
        isLoading={getTagDocumentsLoadingByKey(activeTagDocumentsKey)}
        onClose={closeTagDocumentsModal}
        open={Boolean(tagDocumentsModalState)}
        sectionDisplayName={tagDocumentsModalState?.sectionDisplayName || ""}
        tagColor={tagDocumentsModalState?.tag?.color || "#1f2937"}
        tagDescription={tagDocumentsModalState?.tag?.description}
        tagName={tagDocumentsModalState?.tag?.name || ""}
      />
    </div>
  );
}

export default SectionGeneratorPanel;
